import React, { useEffect, useState } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { debounce } from '@mui/material/utils';

interface UserEntity {
  name: string;
  displayName?: string;
  email?: string;
}

export const UserEntityPickerSchema = {
  uiOptions: {
    type: 'object',
    properties: {
      title: { type: 'string', title: 'Title', default: 'User' },
      description: {
        type: 'string',
        title: 'Description',
        default: 'Select a user',
      },
    },
  },
  returnValue: { type: 'string' },
};

interface UserEntityPickerProps extends Omit<FieldExtensionComponentProps<string>, 'uiSchema'> {
  uiSchema?: {
    'ui:options'?: {
      title?: string;
      description?: string;
    };
  };
}

export const UserEntityPicker = ({
  onChange,
  schema,
  required,
  formData = '',
  rawErrors,
  uiSchema,
}: UserEntityPickerProps) => {
  const catalogApi = useApi(catalogApiRef);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<UserEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const searchUsers = debounce(async (search: string) => {
    setLoading(true);
    try {
      const filter: Record<string, any> = {
        kind: 'User',
      };

      if (search) {
        filter.anyOf = [
          { 'metadata.name': `*${search}*` },
          { 'metadata.displayName': `*${search}*` },
          { 'spec.profile.email': `*${search}*` },
        ];
      }

      const response = await catalogApi.getEntities({
        filter,
        fields: ['metadata.name', 'metadata.displayName', 'spec.profile.email'],
      });

      const users = response.items.map(entity => {
        const name = String(entity.metadata.name || '');
        const displayName = typeof entity.metadata.displayName === 'string' 
          ? entity.metadata.displayName 
          : name;
        const email = entity.spec?.profile && typeof entity.spec.profile === 'object' && 'email' in entity.spec.profile
          ? String(entity.spec.profile.email)
          : undefined;
        
        return {
          name,
          displayName,
          email,
        } as UserEntity;
      });

      setOptions(users);
    } catch (error) {
      console.error('Error searching users:', error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (open) {
      searchUsers('');
    } else {
      setOptions([]);
    }
  }, [open]);

  const handleInputChange = (_: React.ChangeEvent<{}>, value: string) => {
    setInputValue(value);
    if (value.trim() === '') {
      searchUsers(''); // Show all users when input is cleared
    } else {
      searchUsers(value.trim());
    }
  };

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0 && !formData}
      fullWidth
    >
      <Autocomplete
        id="user-entity-picker"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onInputChange={handleInputChange}
        onChange={(_, value) => onChange(value?.name || '')}
        getOptionSelected={(option, value) => option.name === value.name}
        getOptionLabel={option => 
          option.displayName ? 
          `${option.displayName}${option.email ? ` <${option.email}>` : ''}` : 
          option.name
        }
        options={options}
        loading={loading}
        inputValue={inputValue}
        renderInput={params => (
          <TextField
            {...params}
            label={uiSchema?.['ui:options']?.title || schema?.title}
            variant="standard"
            helperText={uiSchema?.['ui:options']?.description || schema?.description}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </FormControl>
  );
};